import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Input } from "@/components/ui/input";
import { Search, X, Check, ChevronDown } from "lucide-react";
import { trFold, matchesAll } from "@/lib/trFold";

/**
 * SearchPicker — autocomplete-style item picker.
 *
 * Behaviour:
 * • Renders an input; user types ≥ `minChars` characters and a dropdown of
 *   matching items appears below.
 * • `filter` receives (item, query) → boolean; defaults to case-insensitive
 *   match on `getLabel(item)` and `getSubtitle(item)`.
 * • `value` is the selected id; when set, the picker shows a "chip" row with
 *   the selected item + a clear (×) button instead of the search input.
 * • Keyboard: ArrowUp/Down navigate suggestions, Enter selects, Esc closes.
 *
 * Props:
 *   items         : Array<T>
 *   value         : string | ""      (item id)
 *   onChange      : (id: string) => void
 *   getId         : (T) => string
 *   getLabel      : (T) => string
 *   getSubtitle   : (T) => string | ReactNode   (optional)
 *   placeholder   : string
 *   minChars      : number (default 3)
 *   maxSuggestions: number (default 8)
 *   testId        : string   (base data-testid)
 *   disabled      : boolean
 */
export default function SearchPicker({
  items = [],
  value = "",
  onChange,
  getId = (x) => x.id,
  getLabel = (x) => x.name || "",
  getSubtitle,
  placeholder = "2 harf yazın…",
  minChars = 2,
  maxSuggestions = 8,
  filter,
  testId = "search-picker",
  disabled = false,
  onSearch,           // optional: async (query) => Promise<item[]> — server-side search
  searchDebounceMs = 300,
  selectedItem,       // optional: object to show when selected item is not in `items` (server-side mode)
}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [remoteMatches, setRemoteMatches] = useState([]);
  const [remoteLoading, setRemoteLoading] = useState(false);
  const inputRef = useRef(null);
  const rootRef = useRef(null);
  const remoteReqIdRef = useRef(0);
  const dropRef = useRef(null);
  const [pos, setPos] = useState(null);

  const selected =
    items.find((it) => getId(it) === value) ||
    (selectedItem && getId(selectedItem) === value ? selectedItem : null);

  // Client-side filter (used when onSearch is not provided)
  const localMatches = useMemo(() => {
    if (onSearch) return [];
    if (q.trim().length < minChars) return [];
    const fn = filter || ((it) => {
      const label = getLabel(it);
      const sub = (getSubtitle && getSubtitle(it)) || "";
      return matchesAll(q, [label, sub]);
    });
    return items.filter((it) => fn(it, q)).slice(0, maxSuggestions);
  }, [q, items, minChars, maxSuggestions, filter, getLabel, getSubtitle, onSearch]);

  // Server-side debounced fetch (used when onSearch is provided)
  useEffect(() => {
    if (!onSearch) return;
    if (q.trim().length < minChars) {
      setRemoteMatches([]);
      setRemoteLoading(false);
      return;
    }
    const reqId = ++remoteReqIdRef.current;
    setRemoteLoading(true);
    const t = setTimeout(async () => {
      try {
        const result = await onSearch(q.trim());
        // Only apply if still the latest request
        if (reqId === remoteReqIdRef.current) {
          const arr = Array.isArray(result) ? result : [];
          setRemoteMatches(arr.slice(0, maxSuggestions));
        }
      } catch {
        if (reqId === remoteReqIdRef.current) setRemoteMatches([]);
      } finally {
        if (reqId === remoteReqIdRef.current) setRemoteLoading(false);
      }
    }, searchDebounceMs);
    return () => clearTimeout(t);
  }, [q, onSearch, minChars, maxSuggestions, searchDebounceMs]);

  const matches = onSearch ? remoteMatches : localMatches;

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => {
      const path = e.composedPath ? e.composedPath() : [e.target];
      if (rootRef.current && !path.includes(rootRef.current) && !(dropRef.current && path.includes(dropRef.current))) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => { setHighlight(0); }, [q]);

  // Dropdown portal: tablo/kart overflow'u ve sticky footer listeyi kırpmasın (Reçete kumaş araması)
  const showDrop = open && q.length > 0 && !selected;
  useLayoutEffect(() => {
    if (!showDrop || !rootRef.current) { setPos(null); return; }
    const host = rootRef.current.closest('[role="dialog"]') || document.body;
    const update = () => {
      const r = rootRef.current?.getBoundingClientRect();
      if (!r) return;
      const tf = host !== document.body && getComputedStyle(host).transform !== "none";
      const h = tf ? host.getBoundingClientRect() : { top: 0, left: 0 };
      setPos({ host, top: r.bottom + 4 - h.top + (tf ? host.scrollTop : 0), left: r.left - h.left + (tf ? host.scrollLeft : 0), width: r.width, fixed: !tf });
    };
    update();
    window.addEventListener("resize", update);
    document.addEventListener("scroll", update, true);
    return () => { window.removeEventListener("resize", update); document.removeEventListener("scroll", update, true); };
  }, [showDrop, q, selected]);
  const floating = (node) => pos ? createPortal(
    <div ref={dropRef} style={{ position: pos.fixed ? "fixed" : "absolute", top: pos.top, left: pos.left, width: Math.max(pos.width, 240), zIndex: 1000 }}>{node}</div>,
    pos.host) : null;

  const pick = (item) => {
    onChange?.(getId(item));
    setQ(""); setOpen(false);
    inputRef.current?.blur();
  };

  const clear = () => {
    onChange?.("");
    setQ(""); setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 10);
  };

  const onKey = (e) => {
    if (!open || matches.length === 0) {
      if (e.key === "Enter" && !selected && q.length >= minChars && items.length > 0) {
        e.preventDefault();
      }
      return;
    }
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => (h + 1) % matches.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => (h - 1 + matches.length) % matches.length); }
    else if (e.key === "Enter") { e.preventDefault(); pick(matches[highlight]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  // Selected chip view
  if (selected) {
    return (
      <div ref={rootRef} className="relative" data-testid={`${testId}-selected`}>
        <div className="flex items-center justify-between gap-2 bg-blue-950/30 border border-blue-800 rounded-sm px-3 py-2">
          <div className="min-w-0 flex items-center gap-2">
            <Check className="w-3.5 h-3.5 text-blue-400 shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-50 truncate">{getLabel(selected)}</div>
              {getSubtitle && (
                <div className="text-[10px] text-slate-400 truncate">{getSubtitle(selected)}</div>
              )}
            </div>
          </div>
          {!disabled && (
            <button
              type="button"
              data-testid={`${testId}-clear`}
              onClick={clear}
              className="text-slate-500 hover:text-red-400 p-1 shrink-0"
              title="Temizle"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div ref={rootRef} className="relative" data-testid={`${testId}-root`}>
      <div className="relative">
        <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <Input
          ref={inputRef}
          data-testid={`${testId}-input`}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder={placeholder}
          disabled={disabled}
          className="bg-slate-900 border-slate-800 pl-9 pr-8 rounded-sm text-slate-100 h-10"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          // Tarayıcıların (Chrome/Edge) "kart bilgileri" veya "adres" otomatik
          // doldurmasını engelle. Bunlar 'name' veya benzeri özniteliklere göre
          // eşleşme yaptığı için form-type ipucu + unique name veriyoruz.
          name={`search-${testId}-${Math.random().toString(36).slice(2, 8)}`}
          data-lpignore="true"
          data-form-type="other"
        />
        <ChevronDown className="w-3.5 h-3.5 text-slate-600 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
      </div>

      {open && q.length >= minChars && floating(
        <div
          data-testid={`${testId}-dropdown`}
          className="max-h-72 overflow-y-auto rounded-sm border border-slate-800 bg-slate-950 shadow-2xl"
        >
          {matches.length === 0 ? (
            <div className="text-xs text-slate-500 italic px-3 py-4 text-center">
              {onSearch && remoteLoading ? "Aranıyor…" : `"${q}" ile eşleşen kayıt bulunamadı.`}
            </div>
          ) : (
            matches.map((item, i) => (
              <button
                key={getId(item)}
                type="button"
                data-testid={`${testId}-option-${getId(item)}`}
                onMouseEnter={() => setHighlight(i)}
                // Dokunmatikte input blur/klavye kapanması seçimi kaçırmasın
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(item)}
                className={`w-full text-left px-3 py-2 border-b border-slate-800 last:border-b-0 flex items-center gap-2 transition-colors ${
                  i === highlight ? "bg-blue-950/40" : "hover:bg-slate-900"
                }`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-slate-100 truncate">
                    <HighlightText text={String(getLabel(item))} q={q} />
                  </div>
                  {getSubtitle && (
                    <div className="text-[10px] text-slate-500 truncate">
                      {typeof getSubtitle(item) === "string"
                        ? <HighlightText text={String(getSubtitle(item))} q={q} />
                        : getSubtitle(item)}
                    </div>
                  )}
                </div>
                {i === highlight && (
                  <span className="text-[9px] uppercase font-mono text-blue-400">Enter</span>
                )}
              </button>
            ))
          )}
        </div>
      )}
      {open && q.length > 0 && q.length < minChars && floating(
        <div className="rounded-sm border border-slate-800 bg-slate-950 shadow-2xl px-3 py-2 text-xs text-slate-500 italic">
          En az {minChars} karakter yazın…
        </div>
      )}
    </div>
  );
}

function HighlightText({ text, q }) {
  if (!q) return text;
  const idx = trFold(text).indexOf(trFold(q));
  if (idx < 0) return text;
  return (
    <>
      {text.slice(0, idx)}
      <span className="bg-blue-500/30 text-blue-200 font-semibold">{text.slice(idx, idx + q.length)}</span>
      {text.slice(idx + q.length)}
    </>
  );
}
